// EVA-221: the OpEx/CapEx grids store stable enum CODES but show human labels.
// The backend passes `optionLists` = [{ column, options: [{ value, label }] }]
// (array form so the snake_case column names survive humps `camelizeKeys`, which
// only rewrites object KEYS — `column`/`options`/`value`/`label` are unchanged and
// the string values are never touched). This builds the label source arrays and
// the code↔label converters the grid needs.
import { autocompleteRenderer } from 'handsontable/renderers'

const ENUM_COLUMNS = new Set([
  'input_method', 'amount_basis', 'calculation_scope', 'repeat', 'start_rule', 'end_rule', 'capex_type'
])

export function buildEnumOptions(optionLists) {
  const byColumn = {}
  ;(optionLists || []).forEach((entry) => {
    if (entry && entry.column) byColumn[entry.column] = entry.options || []
  })

  const codeToLabel = {}
  const labelToCode = {}
  Object.entries(byColumn).forEach(([column, opts]) => {
    codeToLabel[column] = {}
    labelToCode[column] = {}
    opts.forEach(({ value, label }) => {
      codeToLabel[column][value] = label
      labelToCode[column][label] = value
    })
  })

  // Ordered label list for a column's dropdown source.
  const labelsFor = (column) => (byColumn[column] || []).map((o) => o.label)
  // Stored code -> shown label (fall back to the raw value so a legacy/unknown
  // code stays visible rather than rendering blank).
  const labelFor = (column, code) => codeToLabel[column]?.[code] ?? (code ?? '')
  // A label (from the dropdown or a paste) -> stored code. Passes the value
  // through untouched when it is already a code or unrecognised — the backend
  // inclusion validation is the final guard.
  const codeFor = (column, val) => {
    if (val == null) return val
    const code = labelToCode[column]?.[val]
    return code !== undefined ? code : val
  }
  // Map an array of codes to labels (for the dynamically filtered start/end sources).
  const labelsOf = (column, codes) => codes.map((c) => labelFor(column, c))
  // A Handsontable renderer that shows the label while the cell stores the code.
  const renderer = (column) => (instance, td, row, col, prop, value, cellProps) =>
    autocompleteRenderer(instance, td, row, col, prop, labelFor(column, value), cellProps)

  return {
    isEnumColumn: (column) => ENUM_COLUMNS.has(column),
    labelsFor, labelFor, codeFor, labelsOf, renderer
  }
}
